import React, { useState, useEffect } from "react";
import { DollarSign, RefreshCw, Plus, Edit2, Trash2, X } from "lucide-react";
import { useTranslation } from "../../i18n/hooks/useTranslation";
import { getEarningsRateTypeColor, getEarningsRateStatusColor } from "../../utils/statusColors";

interface EarningsRate {
  _id: string;
  name: string;
  displayName?: string;
  earningsType:
    | "ordinary_earnings"
    | "overtime_earnings"
    | "allowance"
    | "bonus"
    | "commission"
    | "other";
  rateType: "multiple" | "rate_per_unit" | "fixed_amount";
  ratePerUnit?: number;
  multiple?: number;
  fixedAmount?: number;
  expenseAccountCode?: string;
  xeroEarningsRateId?: string;
  syncedFromXero: boolean;
  isActive: boolean;
}

export const EarningsRates: React.FC = () => {
  const { t } = useTranslation();
  const [earningsRates, setEarningsRates] = useState<EarningsRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRate, setEditingRate] = useState<EarningsRate | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [earningsType, setEarningsType] = useState<
    | "ordinary_earnings"
    | "overtime_earnings"
    | "allowance"
    | "bonus"
    | "commission"
    | "other"
  >("ordinary_earnings");
  const [rateType, setRateType] = useState<
    "multiple" | "rate_per_unit" | "fixed_amount"
  >("rate_per_unit");
  const [ratePerUnit, setRatePerUnit] = useState("");
  const [multiple, setMultiple] = useState("");
  const [fixedAmount, setFixedAmount] = useState("");
  const [expenseAccountCode, setExpenseAccountCode] = useState("");
  const [isActive, setIsActive] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    fetchEarningsRates();
  }, []);

  const fetchEarningsRates = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/earnings-rates`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setEarningsRates(data.data || []);
      } else {
        setError(data.message || "Failed to fetch earnings rates");
      }
    } catch (err) {
      console.error("Fetch earnings rates error:", err);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setDisplayName("");
    setEarningsType("ordinary_earnings");
    setRateType("rate_per_unit");
    setRatePerUnit("");
    setMultiple("");
    setFixedAmount("");
    setExpenseAccountCode("");
    setIsActive(true);
    setEditingRate(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (rate: EarningsRate) => {
    if (rate.syncedFromXero) {
      setError(
        "Cannot edit earnings rates synced from Xero. Changes must be made in Xero.",
      );
      return;
    }
    setEditingRate(rate);
    setName(rate.name);
    setDisplayName(rate.displayName || "");
    setEarningsType(rate.earningsType);
    setRateType(rate.rateType);
    setRatePerUnit(rate.ratePerUnit?.toString() || "");
    setMultiple(rate.multiple?.toString() || "");
    setFixedAmount(rate.fixedAmount?.toString() || "");
    setExpenseAccountCode(rate.expenseAccountCode || "");
    setIsActive(rate.isActive);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const payload: any = {
        name,
        displayName: displayName || undefined,
        earningsType,
        rateType,
        expenseAccountCode: expenseAccountCode || undefined,
        isActive,
      };

      // Add rate-specific field based on rateType
      if (rateType === "rate_per_unit") {
        if (!ratePerUnit || parseFloat(ratePerUnit) <= 0) {
          setError("Rate per unit must be greater than 0");
          setSubmitting(false);
          return;
        }
        payload.ratePerUnit = parseFloat(ratePerUnit);
      } else if (rateType === "multiple") {
        if (!multiple || parseFloat(multiple) <= 0) {
          setError("Multiple must be greater than 0");
          setSubmitting(false);
          return;
        }
        payload.multiple = parseFloat(multiple);
      } else if (rateType === "fixed_amount") {
        if (!fixedAmount || parseFloat(fixedAmount) <= 0) {
          setError("Fixed amount must be greater than 0");
          setSubmitting(false);
          return;
        }
        payload.fixedAmount = parseFloat(fixedAmount);
      }

      const url = editingRate
        ? `${apiUrl}/api/earnings-rates/${editingRate._id}`
        : `${apiUrl}/api/earnings-rates`;

      const method = editingRate ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setShowModal(false);
        resetForm();
        await fetchEarningsRates();
      } else {
        setError(data.message || "Failed to save earnings rate");
      }
    } catch (err) {
      console.error("Save earnings rate error:", err);
      setError("Failed to connect to server");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (rate: EarningsRate) => {
    if (rate.syncedFromXero) {
      setError(
        "Cannot delete earnings rates synced from Xero. Changes must be made in Xero.",
      );
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${rate.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/earnings-rates/${rate._id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        await fetchEarningsRates();
      } else {
        setError(data.message || "Failed to delete earnings rate");
      }
    } catch (err) {
      console.error("Delete earnings rate error:", err);
      setError("Failed to connect to server");
    }
  };

  const formatEarningsType = (type: string) => {
    return type
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatRateDisplay = (rate: EarningsRate) => {
    if (rate.rateType === "multiple" && rate.multiple) {
      return `${rate.multiple}x Base Rate`;
    } else if (rate.rateType === "rate_per_unit" && rate.ratePerUnit) {
      return `$${rate.ratePerUnit.toFixed(2)}/hour`;
    } else if (rate.rateType === "fixed_amount" && rate.fixedAmount) {
      return `$${rate.fixedAmount.toFixed(2)} Fixed`;
    }
    return "Not set";
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600">
        <div className="p-6 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DollarSign className="w-6 h-6 text-primary-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Earnings Rates
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manage payroll earnings rates and pay types
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={openCreateModal}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Create</span>
            </button>
            <button
              onClick={fetchEarningsRates}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                Loading earnings rates...
              </p>
            </div>
          ) : earningsRates.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                No earnings rates found
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
                Create a new earnings rate or sync from Xero to import earnings
                rates
              </p>
              <button
                onClick={openCreateModal}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Create Earnings Rate
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Expense Account
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  {earningsRates.map((rate) => (
                    <tr
                      key={rate._id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {rate.name}
                        </div>
                        {rate.displayName && rate.displayName !== rate.name && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {rate.displayName}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEarningsRateTypeColor(rate.earningsType)}`}
                        >
                          {formatEarningsType(rate.earningsType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {formatRateDisplay(rate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {rate.expenseAccountCode || "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {rate.syncedFromXero ? (
                          <span className="inline-flex items-center text-xs text-accent-600 dark:text-accent-400">
                            <img
                              src="/xero.svg"
                              alt="Xero"
                              className="w-4 h-4 mr-1"
                            />
                            Xero
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Manual
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEarningsRateStatusColor(rate.isActive)}`}
                        >
                          {rate.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openEditModal(rate)}
                            disabled={rate.syncedFromXero}
                            className={`text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 ${
                              rate.syncedFromXero
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                            title={
                              rate.syncedFromXero
                                ? "Cannot edit Xero-synced rates"
                                : "Edit"
                            }
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(rate)}
                            disabled={rate.syncedFromXero}
                            className={`text-error-600 dark:text-error-400 hover:text-error-800 dark:hover:text-error-300 ${
                              rate.syncedFromXero
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                            title={
                              rate.syncedFromXero
                                ? "Cannot delete Xero-synced rates"
                                : "Delete"
                            }
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingRate ? "Edit Earnings Rate" : "Create Earnings Rate"}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Earnings Type <span className="text-error-500">*</span>
                  </label>
                  <select
                    value={earningsType}
                    onChange={(e) => setEarningsType(e.target.value as any)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="ordinary_earnings">Ordinary Earnings</option>
                    <option value="overtime_earnings">Overtime Earnings</option>
                    <option value="allowance">Allowance</option>
                    <option value="bonus">Bonus</option>
                    <option value="commission">Commission</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rate Type <span className="text-error-500">*</span>
                  </label>
                  <select
                    value={rateType}
                    onChange={(e) => {
                      setRateType(e.target.value as any);
                      // Clear other rate fields when switching type
                      if (e.target.value === "rate_per_unit") {
                        setMultiple("");
                        setFixedAmount("");
                      } else if (e.target.value === "multiple") {
                        setRatePerUnit("");
                        setFixedAmount("");
                      } else {
                        setRatePerUnit("");
                        setMultiple("");
                      }
                    }}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="rate_per_unit">
                      Rate Per Unit (Hourly)
                    </option>
                    <option value="multiple">Multiple of Base Rate</option>
                    <option value="fixed_amount">Fixed Amount</option>
                  </select>
                </div>

                {rateType === "rate_per_unit" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Rate Per Unit ($/hour){" "}
                      <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={ratePerUnit}
                      onChange={(e) => setRatePerUnit(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {rateType === "multiple" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Multiple <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={multiple}
                      onChange={(e) => setMultiple(e.target.value)}
                      required
                      placeholder="e.g., 1.5 for 1.5x base rate"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {rateType === "fixed_amount" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Fixed Amount ($) <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={fixedAmount}
                      onChange={(e) => setFixedAmount(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Expense Account Code
                  </label>
                  <input
                    type="text"
                    value={expenseAccountCode}
                    onChange={(e) => setExpenseAccountCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={isActive ? "active" : "inactive"}
                    onChange={(e) => setIsActive(e.target.value === "active")}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "Saving..." : editingRate ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
